/**
 * PromptRowExpanded — the edit-open state of a prompt row (shown when clicked).
 *
 * Displays: editable text textarea, tags chip editor, place trigger (lot E: multi Pick,
 * FamilyCostLine, zone variants + unticked places deleted), language trigger
 * (lot D: Pick + LanguagePickRows, FamilyCostLine, TranslationReview, LanguageRemoveDialog),
 * intent display, level badge (referral), criteria/target
 * line (image/comparison, read-only — lot F fills details),
 * action row (Désactiver / Supprimer / Relancer), and « Enregistrer ».
 *
 * The component calls the API directly to keep loading state inline.
 *
 * @see B-popup-frontend.md step 11c, UI-SPEC §2.6
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  BrandQuery,
  BrandQueryAxis,
  BrandStrategy,
  AllowanceFigures,
  CreateZoneVariantsResponse,
  FirstRunNotice,
  Location,
  SaveWarnings,
  TargetZone,
  TranslationProposal,
} from '@traaker/shared';
import { effectiveZoneMeta, placeKey, promptPlaceKey, zoneDisplayLabel } from '@traaker/shared';
import type { Family, LimitSlot, PromptAllowanceFigures } from './promptStudioModel';
import { limitReason, needRowsReason, AXIS_COLOR, MONDIAL, knownZones, promptPickerZones, placeOptionsOf, fitsMargin, slotText } from './promptStudioModel';
import {
  familyGrid,
  familyLanguages,
  familyPlaces,
  languageName,
  lockedPlaces,
  placePlan,
  placeRowsAdded,
  rowsAtPlaces,
  rowsOfLanguage,
  togglePlace,
  translatePrecheck,
  zoneRowErrors,
} from './variantModel';
import { LanguagePickRows } from './LanguagePickRows';
import { LanguageRemoveDialog, deleteRows } from './LanguageRemoveDialog';
import { FamilyCostLine } from './FamilyCostLine';
import { TranslationReview, previewRefusal, saveRefusal } from './TranslationReview';
import type { Refusal } from './TranslationReview';
import { LimitReasonLine } from './LimitReasonLine';
import { QuotaWarningDialog } from './QuotaWarningDialog';
import { Flag } from '../../pages/dashboard/Flag';
import { languageFlag } from '../../pages/dashboard/geoFlags';
import { brandQueriesApi } from '../../services/brand-queries';
import { LocChip } from './PromptRow';
import { Pick } from './Pick';
import type { PlaceOption } from './Pick';
import { LevelLine, MetaLine } from './ClassificationLines';
import { CriteriaChips, TopicReviewBlock } from './ImageReviewBlock';
import { vocabFor } from './coverageModel';
import { targetsService } from '../../services/targets';

// ---------------------------------------------------------------------------
// DeleteConfirmDialog (overlay, UI-SPEC §2.6)
// ---------------------------------------------------------------------------

interface DeleteConfirmDialogProps {
  family: Family;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
}

function DeleteConfirmDialog({ family, onConfirm, onCancel, loading, error }: DeleteConfirmDialogProps) {
  const n = family.rows.length;
  const bodyText = n > 1
    ? `Cette question et ses ${n} prompts (langues et lieux) ne seront plus mesurés et disparaissent de la liste. Leurs résultats passés restent consultables.`
    : 'Il ne sera plus mesuré et disparaît de la liste. Ses résultats passés restent consultables.';

  return (
    // Full-stage overlay per §1.1
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(11,11,15,.3)',
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onCancel}
    >
      <Box
        sx={{
          width: 440,
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: 3,
          boxShadow: '0 24px 70px rgba(0,0,0,.22)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>
          {/* i18n: promptStudio.delete.title */}
          Supprimer ce prompt ?
        </Box>
        <Box sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1 }}>
          {bodyText}
        </Box>
        <Box sx={{ fontSize: 12.5, color: 'text.disabled', mb: 2 }}>
          {/* i18n: promptStudio.delete.hint */}
          Pour arrêter la mesure sans supprimer, utilisez Désactiver.
        </Box>
        {error && (
          <Box sx={{ fontSize: 12.5, color: 'warning.main', mb: 1 }}>
            {/* i18n: promptStudio.delete.failed */}
            Suppression impossible, réessayez.
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Box
            component="button"
            onClick={onCancel}
            sx={{ fontSize: 13, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2, py: 0.75, bgcolor: 'transparent', cursor: 'pointer' }}
          >
            {/* i18n: common.cancel */}
            Annuler
          </Box>
          <Box
            component="button"
            onClick={() => void onConfirm()}
            disabled={loading}
            sx={{ fontSize: 13, border: 'none', borderRadius: 1, px: 2, py: 0.75, bgcolor: 'error.main', color: '#fff', cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {/* i18n: common.delete */}
            Supprimer
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TagChip (simple inline chip editor, lot B level)
// ---------------------------------------------------------------------------

interface TagChipEditorProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  onSave: (tags: string[]) => Promise<void>;
}

function TagChipEditor({ tags, onChange, onSave }: TagChipEditorProps) {
  const [input, setInput] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleAdd(val: string) {
    const trimmed = val.trim();
    if (!trimmed || tags.includes(trimmed) || tags.length >= 10) return;
    const next = [...tags, trimmed];
    const prev = tags;
    onChange(next);
    setSaveError(null);
    try {
      await onSave(next);
    } catch {
      onChange(prev);
      setSaveError('Tag non enregistré, réessayez.'); // i18n: promptStudio.tag.saveFailed
    }
    setInput('');
  }

  async function handleRemove(tag: string) {
    const next = tags.filter((t) => t !== tag);
    const prev = tags;
    onChange(next);
    setSaveError(null);
    try {
      await onSave(next);
    } catch {
      onChange(prev);
      setSaveError('Tag non enregistré, réessayez.');
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
      <Box sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', width: 48, flexShrink: 0, textTransform: 'uppercase' }}>
        Tags
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1, minH: 32, px: 1, py: 0.25 }}>
        {tags.map((t) => (
          <Chip
            key={t}
            label={t}
            size="small"
            onDelete={() => void handleRemove(t)}
            sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(0,0,0,.06)' }}
          />
        ))}
        {tags.length < 10 && (
          <Box
            component="input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAdd(input); } }}
            onBlur={() => { if (input) void handleAdd(input); }}
            placeholder={tags.length === 0 ? 'Ajouter un tag' : ''}
            maxLength={40}
            sx={{ border: 'none', outline: 'none', fontSize: 11.5, bgcolor: 'transparent', minWidth: 80, flexShrink: 0 }}
          />
        )}
      </Box>
      <Box sx={{ fontSize: 11, color: 'text.disabled' }}>{tags.length} / 10</Box>
      {saveError && (
        <Box sx={{ fontSize: 11.5, color: 'warning.main', width: '100%', ml: 6 }}>{saveError}</Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// LanguageTrigger (UI-SPEC §2.10: the family's flags + `+`)
// ---------------------------------------------------------------------------

interface LanguageTriggerProps {
  codes: string[];
  title: string;
  disabled: boolean;
  onClick: () => void;
}

function LanguageTrigger({ codes, title, disabled, onClick }: LanguageTriggerProps) {
  return (
    <Box component="span" title={title} sx={{ display: 'inline-flex' }}>
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          p: '2px 8px 2px 3px',
          bgcolor: 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          '&:disabled': { pointerEvents: 'none' },
        }}
      >
        {codes.map((c) => {
          const flag = languageFlag(c);
          return flag
            ? <Flag key={c} countryCode={flag} size={16} />
            : <Box key={c} component="span" sx={{ fontSize: 11 }}>{c.toUpperCase()}</Box>;
        })}
        <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>+</Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PlaceTrigger (UI-SPEC §2.10: one location token per place + `+`)
// ---------------------------------------------------------------------------

interface PlaceTriggerProps {
  places: { key: string; zone: TargetZone | null; legacy?: string }[];
  disabled: boolean;
  onClick: () => void;
}

function PlaceTrigger({ places, disabled, onClick }: PlaceTriggerProps) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        p: '2px 8px 2px 3px',
        bgcolor: 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        '&:disabled': { pointerEvents: 'none' },
      }}
    >
      {places.map((p) => <LocChip key={p.key} zone={p.zone} legacyZone={p.legacy} />)}
      <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>+</Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Place pick save (lot E step 11, UI-SPEC §2.6 « only a place change queued »)
// ---------------------------------------------------------------------------

export type PlaceSaveOutcome =
  | { status: 'blocked'; reason: LimitSlot }
  | { status: 'refused'; refusal: Refusal }
  | { status: 'confirm'; warnings: SaveWarnings[] }
  | { status: 'previewFailed' }
  | { status: 'failed'; res?: CreateZoneVariantsResponse }
  | { status: 'saved'; res?: CreateZoneVariantsResponse };

/**
 * The added places: [B] the rows they add above fitsByAxis[axis] → blocked,
 * nothing sent; then ONE `preview: true` call ([B] `refused` → refused, [A]
 * warnings → confirm, unless `confirmed`), then the same body saved (a 409
 * ALLOWANCE_EXCEEDED → refused, nothing written). Then each unticked place's
 * rows, one `DELETE …?reason=deleted` per row (`deleted` skips the rows a
 * failed attempt already removed). An untick alone is never blocked.
 */
export async function savePlaceVariants(a: {
  brandId: string;
  queryId: string;
  axis: BrandQueryAxis;
  zones: TargetZone[];
  rowsAdded: number;
  remove: BrandQuery[];
  deleted: Set<string>;
  figures: PromptAllowanceFigures | null;
  isAgency: boolean;
  confirmed: boolean;
}): Promise<PlaceSaveOutcome> {
  let res: CreateZoneVariantsResponse | undefined;
  if (a.zones.length > 0) {
    const reason = a.isAgency ? null : needRowsReason(a.rowsAdded, a.figures, a.axis);
    if (reason) return { status: 'blocked', reason };
    const body = { axis: a.axis, zones: a.zones };
    if (!a.confirmed) {
      let pre: CreateZoneVariantsResponse;
      try {
        pre = await brandQueriesApi.createZoneVariants(a.brandId, a.queryId, { ...body, preview: true });
      } catch {
        return { status: 'previewFailed' };
      }
      const refusal = previewRefusal(pre, a.isAgency, a.figures, a.axis);
      if (refusal) return { status: 'refused', refusal };
      if (a.isAgency && pre.warnings?.length) return { status: 'confirm', warnings: pre.warnings };
    }
    try {
      res = await brandQueriesApi.createZoneVariants(a.brandId, a.queryId, body);
    } catch (err) {
      const refusal = saveRefusal(err, a.axis);
      return refusal ? { status: 'refused', refusal } : { status: 'failed' };
    }
  }
  try {
    await deleteRows(a.brandId, a.remove, a.deleted);
  } catch {
    return { status: 'failed', res };
  }
  return { status: 'saved', res };
}

// ---------------------------------------------------------------------------
// PromptRowExpanded
// ---------------------------------------------------------------------------

export interface PromptRowExpandedProps {
  family: Family;
  brandId: string;
  locations: Location[];
  /** Place search language (brand working language; also defaults an unlabelled row's language). */
  lang?: string;
  /** The popup's GET …/allowances figures (cost line « il reste », translate pre-check). */
  promptFigures?: PromptAllowanceFigures | null;
  isAgency?: boolean;
  /** A creating save's `firstRun` → the popup's first-run strip. */
  onFirstRun?: (firstRun?: FirstRunNotice[]) => void;
  onChanged?: () => void;
  onClose?: () => void;
  /** The brand's vocabularies: the theme / use case and criteria chips (owner, 2026-10-30). */
  strategy?: BrandStrategy;
}

export function PromptRowExpanded({
  family,
  brandId,
  locations,
  lang = 'fr',
  promptFigures = null,
  isAgency = false,
  onFirstRun,
  onChanged,
  onClose,
  strategy,
}: PromptRowExpandedProps) {
  const { t, i18n } = useTranslation();
  const { root } = family;
  const isPending = root.axis === 'pending';
  const isImage = root.axis === 'narrative' || root.axis === 'comparison';
  const isReferral = root.axis === 'referral';

  const [text, setText] = useState(root.text);
  const [tags, setTags] = useState<string[]>(root.tags ?? []);
  // Theme / use case / criteria: saved on click, family-wide (C11 direct edit), reverted on failure.
  const [topic, setTopic] = useState<string | undefined>(root.topic);
  const [criteria, setCriteria] = useState<string[]>(root.criteria ?? []);
  const [classSaving, setClassSaving] = useState(false);
  const [classError, setClassError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveLimit, setSaveLimit] = useState<LimitSlot | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingRow, setDeletingRow] = useState(false);
  const [disabling, setDisabling] = useState(false);
  // Lot E: the ticked places (null = nothing picked), autocomplete picks, the save's outcome.
  const [ticked, setTicked] = useState<string[] | null>(null);
  const [pickedZones, setPickedZones] = useState<TargetZone[]>([]);
  const [pickOpen, setPickOpen] = useState(false);
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeRefusal, setPlaceRefusal] = useState<Refusal | null>(null);
  const [placeRowErrs, setPlaceRowErrs] = useState<LimitSlot[]>([]);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [quotaWarnings, setQuotaWarnings] = useState<SaveWarnings[] | null>(null);
  const deletedRef = useRef(new Set<string>());
  const [targetName, setTargetName] = useState<{ label: string; category: string } | null>(null);
  // Lot D: queued languages (« à traduire »), the language picker, the translation review.
  const [queued, setQueued] = useState<string[]>([]);
  const [langPickOpen, setLangPickOpen] = useState(false);
  const [langQuery, setLangQuery] = useState('');
  const [removeLang, setRemoveLang] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<'rateLimited' | 'failed' | null>(null);
  const [proposals, setProposals] = useState<TranslationProposal[] | null>(null);
  useEffect(() => {
    if (!root.targetId) return;
    let live = true;
    targetsService.getByIds(brandId, [{ targetId: root.targetId, ...(root.targetCategory ? { category: root.targetCategory } : {}) }]).then(
      (res) => {
        const it = res.items[0];
        // i18n: promptStudio.targetChip.unresolved
        if (live) setTargetName(it ? { label: it.label ?? it.url ?? it.targetId, category: it.category } : { label: 'Cible non résolue', category: '' });
      },
      () => {},
    );
    return () => { live = false; };
  }, [brandId, root.targetId, root.targetCategory]);

  const textChanged = text !== root.text;
  const rowPlaceKey = placeKey(root, locations);

  // Lot E. The family's places (key → effective zone, or the stored zone of an
  // unresolved legacy row), the pick's plan (UI-SPEC §2.10 « lot E (multi) »).
  const famPlaces = new Map<string, { zone: TargetZone | null; legacy?: string }>();
  for (const r of family.rows) {
    if (r.status !== 'active') continue;
    const k = placeKey(r, locations);
    const zone = effectiveZoneMeta(r, locations);
    if (!famPlaces.has(k)) famPlaces.set(k, { zone, legacy: zone ? undefined : r.zone });
  }
  const current = [...famPlaces.keys()];
  const placeCtx = { current, rootKey: rowPlaceKey };
  const plan = placePlan(current, ticked);
  const placeChanged = plan.kind !== 'none';
  const placeUnchanged = ticked !== null && !placeChanged;
  const dirty = textChanged || placeChanged;
  const addKeys = plan.kind === 'variants' ? plan.add : [];
  const removeKeys = plan.kind === 'variants' ? plan.remove : [];
  const rowsAdded = placeRowsAdded(family, lang, addKeys.length);

  // Lot D. A language change and a text / place change never coexist: queuing a
  // language locks the text and the place; a text or place edit disables the language trigger.
  const axis = root.axis as BrandQueryAxis;
  const languages = familyLanguages(family, lang);
  const nPlaces = familyPlaces(family, locations).length;
  const left = isAgency || isPending ? null : fitsMargin(promptFigures, axis)?.lo ?? null;
  const precheck = left === null || queued.length === 0
    ? null
    : translatePrecheck(queued.length, nPlaces, promptFigures, axis);
  const grid = familyGrid(family, locations, lang, queued, []);
  const langLocked = queued.length > 0 || proposals !== null;
  // [B] the place pick greyed BEFORE any call (UI-SPEC §2.6); an untick or a replace never is.
  const placeCheck = isAgency || addKeys.length === 0 ? null : needRowsReason(rowsAdded, promptFigures, axis);
  const placeGrid = familyGrid(family, locations, lang, [], addKeys, removeKeys);
  // A refused preview or a 409 greys until the pick changes (reason from its figuresAfter).
  const placeLimit = placeRefusal ? placeRefusal.reason : placeCheck;
  const greyed = !!saveLimit || !!placeCheck || !!placeRefusal;

  const placeZones = placeOptionsOf(
    [...[...famPlaces.values()].map((p) => p.zone), ...pickedZones],
    promptPickerZones(knownZones([], [], locations), locations),
  );
  const legacyOptions: PlaceOption[] = [...famPlaces]
    .filter(([k, p]) => !p.zone && k !== MONDIAL)
    .map(([key, p]) => ({ key, zone: null, label: p.legacy ?? key }));
  const placeOptions: PlaceOption[] = [
    ...legacyOptions,
    ...[...placeZones].map(([key, zone]) => ({ key, zone, label: zoneDisplayLabel(zone) })),
  ];
  const zoneOf = (key: string): TargetZone | null => (key === MONDIAL ? null : placeZones.get(key) ?? null);
  const shownPlaces = (ticked ?? current).map((key) => ({ key, zone: zoneOf(key), legacy: famPlaces.get(key)?.legacy }));

  function onPlaceToggle(key: string) {
    setPlaceRefusal(null);
    setPlaceRowErrs([]);
    setPreviewFailed(false);
    setSaveError(null);
    // A pending root cannot take variants (the route refuses it): its pick stays the one-place replace.
    if (isPending) {
      setTicked([key]);
      setPickOpen(false);
      return;
    }
    setTicked(togglePlace(placeCtx, ticked ?? current, key));
  }
  const axisColor = root.axis !== 'pending'
    ? AXIS_COLOR[root.axis as BrandQueryAxis] ?? '#888'
    : '#f59e0b';

  // Theme / use case / criteria (owner, 2026-10-30): PATCH { axis, topic } | { axis, criteria }.
  async function saveClassification(change: { topic?: string | null; criteria?: string[] }, revert: () => void) {
    setClassSaving(true);
    setClassError(false);
    try {
      await brandQueriesApi.update(brandId, root.queryId, { axis: root.axis as BrandQueryAxis, ...change });
      onChanged?.();
    } catch {
      revert();
      setClassError(true);
    } finally {
      setClassSaving(false);
    }
  }

  // Save (legacy edit): PATCH { axis, text, tags }; place-only (direct edit): PATCH { axis, zoneMeta }
  async function handleSave() {
    if (!dirty) return;
    setSaving(true);
    setSaveError(null);
    setSaveLimit(null);
    try {
      const axis = root.axis as BrandQueryAxis | 'pending';
      await brandQueriesApi.update(
        brandId,
        root.queryId,
        textChanged ? { axis, text, tags } : { axis, zoneMeta: plan.kind === 'replace' ? zoneOf(plan.key) : null },
      );
      onChanged?.();
      onClose?.();
    } catch (err: unknown) {
      const e = err as { status?: number; body?: { figuresAfter?: AllowanceFigures; message?: string } };
      if (e?.status === 409 && e?.body?.figuresAfter) {
        setSaveLimit(limitReason(e.body.figuresAfter, 'promptRequests'));
      } else if (e?.status === 400 && e?.body?.message) {
        setSaveError(e.body.message);
      } else {
        // i18n: promptStudio.edit.saveFailed
        setSaveError('Enregistrement impossible, réessayez.');
      }
    } finally {
      setSaving(false);
    }
  }

  // Lot E « Enregistrer » with added and / or unticked places (never the replace).
  async function handlePlaceSave(confirmed: boolean) {
    if (plan.kind !== 'variants') return;
    setSaving(true);
    setSaveError(null);
    setPreviewFailed(false);
    setPlaceRowErrs([]);
    const out = await savePlaceVariants({
      brandId,
      queryId: root.queryId,
      axis,
      zones: plan.add.map(zoneOf).filter((z): z is TargetZone => z !== null),
      rowsAdded,
      remove: rowsAtPlaces(family, locations, plan.remove),
      deleted: deletedRef.current,
      figures: promptFigures,
      isAgency,
      confirmed,
    });
    setSaving(false);
    if (out.status === 'saved' || out.status === 'failed') {
      if (out.res) {
        if (out.res.created.length > 0) onFirstRun?.(out.res.firstRun);
        setPlaceRowErrs(zoneRowErrors(out.res, axis));
      }
      if (out.status === 'saved') {
        setTicked(null);
        setPickedZones([]);
      } else {
        setSaveError(t('promptStudio.common.saveFailed'));
      }
      if (out.status === 'saved' || out.res) onChanged?.();
    }
    if (out.status === 'refused') setPlaceRefusal(out.refusal);
    if (out.status === 'confirm') setQuotaWarnings(out.warnings);
    if (out.status === 'previewFailed') setPreviewFailed(true);
  }

  // « Traduire et enregistrer » (lot D step 8): translate the root into the queued languages → review.
  async function handleTranslate() {
    setTranslating(true);
    setTranslateError(null);
    try {
      const res = await brandQueriesApi.translateVariants(brandId, {
        roots: [{ queryId: root.queryId, axis }],
        languages: queued,
      });
      setProposals(res.proposals);
    } catch (err) {
      setTranslateError((err as { status?: number })?.status === 429 ? 'rateLimited' : 'failed');
    } finally {
      setTranslating(false);
    }
  }

  // Désactiver: archive without reason
  async function handleDisable() {
    setDisabling(true);
    try {
      const axis = root.axis as BrandQueryAxis | 'pending';
      await brandQueriesApi.archive(brandId, axis, root.queryId);
      onChanged?.();
      onClose?.();
    } catch {
      setSaveError('Enregistrement impossible, réessayez.');
    } finally {
      setDisabling(false);
    }
  }

  // Supprimer: archive with reason=deleted, variants first, root last
  async function handleDelete() {
    setDeletingRow(true);
    setDeleteError(null);
    try {
      // variants first (all rows except root), then root
      const variants = family.rows.filter((r) => r.queryId !== root.queryId);
      for (const v of variants) {
        await brandQueriesApi.archive(brandId, v.axis as BrandQueryAxis | 'pending', v.queryId, 'deleted');
      }
      await brandQueriesApi.archive(brandId, root.axis as BrandQueryAxis | 'pending', root.queryId, 'deleted');
      onChanged?.();
      setShowDeleteDialog(false);
      onClose?.();
    } catch {
      setDeleteError('Suppression impossible, réessayez.');
    } finally {
      setDeletingRow(false);
    }
  }

  // Save tags (optimistic, done via parent update)
  async function handleSaveTags(newTags: string[]) {
    await brandQueriesApi.update(brandId, root.queryId, {
      axis: root.axis as BrandQueryAxis | 'pending',
      tags: newTags,
    });
    onChanged?.();
  }

  // Relancer le classement (pending rows) — C16: no text, just re-runs classification
  // « Relancer le classement »: `POST :queryId/classify` SAVES NOTHING (C11 19a), so its result is
  // committed through the reviewed PATCH (19d), which moves the row off « En attente ». Until
  // 2026-11-04 the result was discarded and the button did nothing.
  const [reclassifying, setReclassifying] = useState(false);
  async function handleRetryClassify() {
    if (reclassifying) return;
    setReclassifying(true);
    setSaveError(null);
    try {
      const c = await brandQueriesApi.classify(brandId, root.queryId, { axis: root.axis });
      if (!c.intent) {
        setSaveError(t('promptStudio.edit.stillPending'));
        return;
      }
      if (c.refused) {
        setSaveError(t('promptStudio.edit.reclassifyRefused'));
        return;
      }
      await brandQueriesApi.update(brandId, root.queryId, {
        axis: root.axis,
        text: root.text,
        intent: c.intent,
        ...(c.language ? { language: c.language } : {}),
        topic: c.topic ?? null,
        ...(c.criteria ? { criteria: c.criteria } : {}),
        ...(c.complements ? { complements: c.complements } : {}),
        targetId: c.targetId ?? null,
      });
      onChanged?.();
    } catch (err) {
      const e = err as { status?: number; body?: { code?: string } };
      setSaveError(
        e?.status === 429
          ? t('promptStudio.composer.rateLimited')
          : e?.status === 409 && e.body?.code === 'ALLOWANCE_EXCEEDED'
            ? t('promptStudio.edit.reclassifyRefused')
            : t('promptStudio.edit.reclassifyFailed'),
      );
    } finally {
      setReclassifying(false);
    }
  }

  return (
    <Box
      sx={{ bgcolor: '#fcfbff', px: 2, pb: 1.5, pl: '35px' }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Editable text */}
      <Box
        component="textarea"
        value={text}
        readOnly={langLocked}
        onChange={(e) => { setText(e.target.value); setTicked(null); }}
        rows={2}
        sx={{
          width: '100%',
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.5,
          border: '1.5px solid #7c3aed',
          borderRadius: 2,
          p: 1,
          resize: 'vertical',
          fontFamily: 'inherit',
          outline: 'none',
          '&:focus': { boxShadow: '0 0 0 3px rgba(124,58,237,.1)' },
        }}
      />
      {/* Char count */}
      <Box sx={{ fontSize: 11, color: text.length > 160 ? '#b45309' : 'text.disabled', textAlign: 'right', mb: 0.5 }}>
        {text.length} / 300
      </Box>

      {isReferral && (
        <LevelLine level={root.level} complements={root.complements} zone={effectiveZoneMeta(root, locations)} />
      )}
      {(root.axis === 'authority' || root.axis === 'referral') && (
        <TopicReviewBlock
          axis={root.axis}
          topicOptions={vocabFor(strategy, root.axis)}
          topic={topic}
          disabled={classSaving}
          onTopicChange={(next) => {
            const prev = topic;
            setTopic(next);
            saveClassification({ topic: next ?? null }, () => setTopic(prev));
          }}
        />
      )}
      {isImage && root.classifiedAt && (
        <>
          <CriteriaChips
            criteriaOptions={vocabFor(strategy, root.axis as BrandQueryAxis)}
            criteria={criteria}
            disabled={classSaving}
            onCriteriaChange={(next) => {
              const prev = criteria;
              setCriteria(next);
              saveClassification({ criteria: next }, () => setCriteria(prev));
            }}
          />
          <MetaLine
            axis={root.axis}
            targetOnly
            target={root.targetId ? targetName ?? undefined : null}
            title="fixé à la validation, modifiez le texte pour le revoir" // i18n: promptStudio.meta.fixedTitle
          />
        </>
      )}
      {classError && (
        <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 0.25 }}>
          {t('promptStudio.common.saveFailed')}
        </Box>
      )}

      {/* Intent line */}
      {root.classifiedAt && root.axis !== 'pending' && (
        <Box sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.5 }}>
          {/* Axis colour dot + intent */}
          <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: axisColor, mr: 0.5, verticalAlign: 'middle' }} />
          {root.intent ?? 'intent inconnu'}
        </Box>
      )}
      {!root.classifiedAt && root.axis !== 'pending' && (
        <Box sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.5 }}>
          {/* i18n: promptStudio.edit.notClassified */}
          non classé
        </Box>
      )}

      {/* Place trigger + language trigger */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
        <PlaceTrigger
          places={shownPlaces}
          disabled={textChanged || langLocked}
          onClick={() => { setPlaceQuery(''); setPickOpen(true); }}
        />
        <LanguageTrigger
          codes={languages}
          title={isPending
            ? t('promptStudio.pick.pendingRoot')
            : t('promptStudio.pick.languagesTitle', { list: languages.map((c) => languageName(c, i18n.language)).join(', ') })}
          disabled={isPending || dirty || proposals !== null}
          onClick={() => { setLangQuery(''); setLangPickOpen(true); }}
        />
      </Box>
      <Pick
        open={langPickOpen}
        kind="language"
        multi
        selected={queued}
        onToggle={() => {}}
        onClose={() => setLangPickOpen(false)}
        searchQuery={langQuery}
        onSearchChange={setLangQuery}
      >
        <LanguagePickRows
          family={family}
          locations={locations}
          workingLanguage={lang}
          queued={queued}
          searchQuery={langQuery}
          onTick={(code) => { setQueued((q) => [...q, code]); setTicked(null); }}
          onUntick={(code) => setQueued((q) => q.filter((c) => c !== code))}
          onRemove={setRemoveLang}
        />
      </Pick>
      {removeLang && (
        <LanguageRemoveDialog
          brandId={brandId}
          language={removeLang}
          rows={rowsOfLanguage(family, removeLang, lang)}
          onDone={() => { setRemoveLang(null); onChanged?.(); }}
          onCancel={() => setRemoveLang(null)}
        />
      )}
      <Pick
        open={pickOpen}
        kind="place"
        multi={!isPending}
        selected={ticked ?? current}
        locked={isPending ? [] : lockedPlaces(placeCtx, ticked ?? current)}
        lockedTitle={(key) => (key === rowPlaceKey ? t('promptStudio.pick.rootPlace') : '')}
        onToggle={onPlaceToggle}
        onClose={() => setPickOpen(false)}
        onPickZone={(zone) => {
          const key = promptPlaceKey(zone);
          setPickedZones((z) => [...z, zone]);
          setPlaceQuery('');
          if (!(ticked ?? current).includes(key)) onPlaceToggle(key);
        }}
        placeOptions={placeOptions}
        searchQuery={placeQuery}
        onSearchChange={setPlaceQuery}
        lang={lang}
      />

      {/* Tag editor */}
      <TagChipEditor tags={tags} onChange={setTags} onSave={handleSaveTags} />

      {proposals ? (
        <TranslationReview
          brandId={brandId}
          root={root}
          proposals={proposals}
          isAgency={isAgency}
          currentFigures={promptFigures}
          onBack={() => setProposals(null)}
          onSaved={(res, created, done) => {
            if (res.created.length > 0) {
              onFirstRun?.(res.firstRun);
              onChanged?.();
            }
            setQueued((q) => q.filter((c) => !created.includes(c)));
            if (done) setProposals(null);
          }}
        />
      ) : (<>
      {/* Save error / limit */}
      {(saveError || saveLimit) && (
        <Box sx={{ fontSize: 11.5, color: 'warning.main', mb: 0.5 }}>
          {saveError ?? saveLimit?.key}
        </Box>
      )}

      {queued.length > 0 && (
        <FamilyCostLine {...grid} added={queued.length * nPlaces} left={left} />
      )}
      {placeChanged && <FamilyCostLine {...placeGrid} added={rowsAdded} left={left} />}

      {/* Action row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        {/* Pending: Relancer le classement */}
        {isPending && (
          <Box
            component="button"
            onClick={() => void handleRetryClassify()}
            disabled={reclassifying}
            sx={{ fontSize: 11.5, color: 'text.secondary', border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, textDecoration: 'underline', '&:disabled': { cursor: 'default', textDecoration: 'none' } }}
          >
            {reclassifying ? t('promptStudio.edit.reclassifying') : t('promptStudio.edit.retryClassify')}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Désactiver */}
        {!isPending && (
          <Box
            component="button"
            onClick={() => void handleDisable()}
            disabled={disabling}
            sx={{ fontSize: 12, color: 'text.secondary', border: 'none', bgcolor: 'transparent', cursor: disabling ? 'wait' : 'pointer', p: 0 }}
          >
            {/* i18n: promptStudio.row.disable */}
            Désactiver
          </Box>
        )}

        {/* Supprimer */}
        <Box
          component="button"
          onClick={() => { setDeleteError(null); setShowDeleteDialog(true); }}
          sx={{ fontSize: 12, color: 'error.main', border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0 }}
        >
          {/* i18n: promptStudio.row.delete */}
          Supprimer
        </Box>

        {queued.length > 0 ? (
          <Tooltip title={precheck ? slotText(t, precheck) : ''} arrow placement="top">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Box
                component="button"
                onClick={() => void handleTranslate()}
                disabled={translating || !!precheck}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#fff',
                  bgcolor: precheck ? 'rgba(0,0,0,.15)' : '#7c3aed',
                  border: 'none',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  opacity: precheck ? 0.4 : 1,
                  cursor: translating ? 'wait' : precheck ? 'not-allowed' : 'pointer',
                  '&:disabled': { pointerEvents: 'none' },
                }}
              >
                {translating ? '…' : t('promptStudio.edit.translateSave')}
              </Box>
            </Box>
          </Tooltip>
        ) : (
        /* Enregistrer */
        <Tooltip
          title={
            placeLimit ? slotText(t, placeLimit)
              : saveLimit ? saveLimit.key
              : placeUnchanged ? t('promptStudio.edit.placeUnchanged') : ''
          }
          arrow
          placement="top"
        >
          <Box component="span" sx={{ display: 'inline-flex', cursor: 'not-allowed' }}>
            <Box
              component="button"
              onClick={() => void (plan.kind === 'variants' ? handlePlaceSave(false) : handleSave())}
              disabled={!dirty || saving || greyed}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: '#fff',
                bgcolor: !dirty || greyed ? 'rgba(0,0,0,.15)' : '#7c3aed',
                border: 'none',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                opacity: greyed ? 0.4 : 1,
                cursor: !dirty || saving || greyed ? 'not-allowed' : 'pointer',
                '&:disabled': { pointerEvents: 'none' },
              }}
            >
              {/* i18n: promptStudio.edit.validate */}
              {saving ? '…' : placeChanged ? t('common.save') : 'Valider le texte'}
            </Box>
          </Box>
        </Tooltip>
        )}
      </Box>
      {precheck && <LimitReasonLine reason={precheck} />}
      {(placeCheck || placeRefusal) && (
        <LimitReasonLine reason={placeRefusal ? placeRefusal.reason : placeCheck} left={placeRefusal?.left} />
      )}
      {previewFailed && (
        <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 0.5 }}>{t('promptStudio.fit.previewFailed')}</Box>
      )}
      {placeRowErrs.map((slot) => (
        <Box key={slot.key} sx={{ fontSize: 11.5, color: '#b45309', mt: 0.5 }}>{slotText(t, slot)}</Box>
      ))}
      {translateError && (
        <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 0.5 }}>
          {t(translateError === 'rateLimited' ? 'promptStudio.trans.rateLimited' : 'promptStudio.common.saveFailed')}
        </Box>
      )}
      </>)}

      <QuotaWarningDialog
        open={quotaWarnings !== null}
        warnings={quotaWarnings}
        onConfirm={() => { setQuotaWarnings(null); void handlePlaceSave(true); }}
        onCancel={() => setQuotaWarnings(null)}
      />

      {/* Delete confirm dialog */}
      {showDeleteDialog && (
        <DeleteConfirmDialog
          family={family}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteDialog(false)}
          loading={deletingRow}
          error={deleteError}
        />
      )}
    </Box>
  );
}
